import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import { GlobalContext } from 'providers/GlobalProvider';
import { ITeam } from 'interfaces/IUser';

interface ITeamOption {
  label: string;
  value: string;
  team: ITeam;
}

export const TeamPicker: React.FC = () => {
  const { cur_user, handleTeamChange } = useContext(GlobalContext);
  const [teamOptions, setTeamOptions] = useState<ITeamOption[]>([]);
  const history = useHistory();

  const formatTeamOptions = (teams: ITeam[]): ITeamOption[] => {
    if (!teams) return [];
    return teams.map((t: ITeam) => ({
      label: t.name ? t.name : '',
      value: t.id,
      team: t,
    }));
  };

  useEffect(() => {
    if (cur_user && cur_user.teams) {
      const userTeams: ITeamOption[] = formatTeamOptions(cur_user.teams);
      setTeamOptions(userTeams);
    }
  }, [cur_user]);

  const selectedValue =
    teamOptions.find((t) => t.value === cur_user.selected_team?.id) || null;

  return (
    <Autocomplete
      id='team-picker-autocomplete'
      options={teamOptions}
      getOptionLabel={(option: ITeamOption) => option.label || ''}
      value={selectedValue}
      isOptionEqualToValue={(option: ITeamOption, value: ITeamOption) =>
        option.value === value.value
      }
      // Handle Selection Change
      onChange={(_event: Event, newValue: ITeamOption) => {
        if (newValue) {
          handleTeamChange(newValue.team).then(() => history.push('/'));
        }
      }}
      // Ensure the dropdown menu appears above other elements
      componentsProps={{
        popper: {
          sx: {
            zIndex: 1300,
            '& .MuiAutocomplete-option': {
              color: 'var(--text-primary-white) !important',
            },
          }, // Higher z-index to sit above sidebar/modals
        },
        paper: {
          sx: {
            color: 'var(--text-primary-white) !important',
          },
        },
      }}
      // Styling Fixes
      sx={{
        width: '100%',
        background: 'var(--bg-colors)',
        borderRadius: '8px',
        color: 'var(--text-primary-white) !important',

        // Target the Input Root (The box itself)
        '& .MuiOutlinedInput-root': {
          height: '45px',
          color: 'var(--text-primary-white) !important',
          paddingRight: '30px !important', // Ensure space for the arrow icon

          '& fieldset': {
            borderColor: '#4a4c51',
          },
          '&:hover fieldset': {
            borderColor: '#fff',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#4895ef',
          },
        },

        // Target the Input Text (Where you type)
        '& .MuiAutocomplete-input': {
          padding: '0px 10px !important', // Reset default padding
          height: '100%',
          boxSizing: 'border-box',
          color: 'var(--text-primary-white) !important',
        },

        // Target the Arrow Icon
        '& .MuiSvgIcon-root': {
          color: '#bdbec1',
        },
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          placeholder='Select Team'
          variant='outlined'
          inputProps={{
            ...params.inputProps, // Keep all default accessibility props
            onKeyDown: (e: Event) => {
              e.stopPropagation(); // Stop 'T' from bubbling to the global app
              if (params.inputProps.onKeyDown) {
                params.inputProps.onKeyDown(e);
              }
            },
          }}
          InputProps={{
            ...params.InputProps,
            style: { padding: '4px 8px' }, // Gentle padding adjustment
          }}
        />
      )}
    />
  );
};
